import {
  AccountBalanceWalletRounded,
  AutoGraphRounded,
  CalendarMonthRounded,
  CalendarTodayRounded,
  CancelRounded,
  CheckCircleRounded,
  DateRangeRounded,
  PointOfSaleRounded,
  ReceiptLongRounded,
  TodayRounded,
  TrendingDownRounded,
  TrendingUpRounded,
  WarningAmberRounded
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FinancialCashFlowResponse,
  FinancialSummaryResponse,
  MetricSummaryInterface,
  SalesSummaryResponse
} from "../../common/interfaces";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import NewVsOldClientsChart from "../../components/charts/NewVsOldClientsChart";
import OrdersByMonthChart from "../../components/charts/OrdersByMonthChart";
import OrdersSummaryDoughnut from "../../components/charts/OrdersPieChart";
import SalesByDayChart from "../../components/charts/SalesByDayChart";
import { FinancialCashFlowChart } from "../../components/charts/FinancialCharts";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  ChartsGrid,
  Container,
  DatePill,
  Header,
  HeaderText,
  MainContent,
  SalesRow,
  SectionLabel,
  StatBody,
  StatCard,
  StatGrid,
  StatIcon
} from "./welcome.styled";

const accents = {
  indigo: "#2b7bff",
  cyan: "#22d3ee",
  violet: "#3b82f6",
  amber: "#fbbf24",
  emerald: "#34d399",
  rose: "#fb7185"
};

const brl = (n: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(n || 0);

const Welcome: React.FC = () => {
  const containerId = "toaster-workorder";
  const { autoError } = usePageToast(containerId, true);

  const { isUser } = useAuth();

  const [metricSummary, setMetricSummary] =
    useState<MetricSummaryInterface | null>(null);
  const [salesSummary, setSalesSummary] =
    useState<SalesSummaryResponse | null>(null);
  const [financialSummary, setFinancialSummary] =
    useState<FinancialSummaryResponse | null>(null);
  const [financialCashFlow, setFinancialCashFlow] =
    useState<FinancialCashFlowResponse | null>(null);

  const fetchFinancialOverview = async () => {
    if (isUser) return;
    try {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1)
        .toISOString()
        .slice(0, 10);
      const to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        .toISOString()
        .slice(0, 10);

      const [summaryRes, cashFlowRes] = await Promise.all([
        api.get<FinancialSummaryResponse>("/financial/summary", {
          params: { from, to, basis: "cash" }
        }),
        api.get<FinancialCashFlowResponse>("/financial/cash-flow", {
          params: { from, to, basis: "cash", groupBy: "day" }
        })
      ]);
      setFinancialSummary(summaryRes.data);
      setFinancialCashFlow(cashFlowRes.data);
    } catch (error) {
      console.error("Erro ao buscar resumo financeiro:", error);
    }
  };

  const fetchMetricSummary = async () => {
    try {
      const response = await api.get("/work-order/summary", {});
      setMetricSummary(response.data);
    } catch (error) {
      const msg = extractErrorMessage(error);
      autoError(msg);
      console.error("Erro ao buscar ordens de serviço:", error);
    }
  };

  const fetchSalesSummary = async () => {
    try {
      const { data } = await api.get<SalesSummaryResponse>(
        "/dashboard/sales-summary"
      );
      setSalesSummary(data);
    } catch (error) {
      console.error("Erro ao buscar resumo de vendas:", error);
    }
  };

  useEffect(() => {
    fetchMetricSummary();
    fetchSalesSummary();
    fetchFinancialOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const today = new Date().toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric"
  });

  const salesStats = [
    {
      label: "Vendas hoje",
      value: salesSummary?.day.count ?? 0,
      sub: brl(salesSummary?.day.revenue ?? 0),
      icon: <TodayRounded />,
      accent: accents.indigo
    },
    {
      label: "Vendas na semana",
      value: salesSummary?.week.count ?? 0,
      sub: brl(salesSummary?.week.revenue ?? 0),
      icon: <DateRangeRounded />,
      accent: accents.cyan
    },
    {
      label: "Vendas no mês",
      value: salesSummary?.month.count ?? 0,
      sub: brl(salesSummary?.month.revenue ?? 0),
      icon: <CalendarMonthRounded />,
      accent: accents.violet
    },
    {
      label: "Vendas no ano",
      value: salesSummary?.year.count ?? 0,
      sub: brl(salesSummary?.year.revenue ?? 0),
      icon: <AutoGraphRounded />,
      accent: accents.emerald
    },
    {
      label: "Canceladas no mês",
      value: salesSummary?.canceledMonth?.count ?? 0,
      sub: brl(salesSummary?.canceledMonth?.revenue ?? 0),
      icon: <CancelRounded />,
      accent: accents.rose
    }
  ];

  const financialStats = [
    {
      label: "Entradas no mês",
      value: brl(financialSummary?.income ?? 0),
      icon: <TrendingUpRounded />,
      accent: accents.emerald
    },
    {
      label: "Saídas no mês",
      value: brl(financialSummary?.expense ?? 0),
      icon: <TrendingDownRounded />,
      accent: accents.rose
    },
    {
      label: "Saldo do mês",
      value: brl(financialSummary?.balance ?? 0),
      icon: <AccountBalanceWalletRounded />,
      accent: accents.indigo
    },
    {
      label: "Despesas pendentes",
      value: brl(financialSummary?.pendingExpense ?? 0),
      icon: <ReceiptLongRounded />,
      accent: accents.amber
    },
    {
      label: "Despesas vencidas",
      value: brl(financialSummary?.overdueExpense ?? 0),
      icon: <WarningAmberRounded />,
      accent: accents.rose
    }
  ];

  const orderStats = [
    {
      label: "Ordem atual",
      value: metricSummary?.current_order_number ?? "00000",
      icon: <ReceiptLongRounded />,
      accent: accents.indigo
    },
    {
      label: "Ordens esta semana",
      value: metricSummary?.week_count ?? "00",
      icon: <DateRangeRounded />,
      accent: accents.cyan
    },
    {
      label: "Ordens este mês",
      value: metricSummary?.month_count ?? "00",
      icon: <CalendarMonthRounded />,
      accent: accents.violet
    },
    {
      label: "Completas no semestre",
      value: metricSummary?.semester_completed ?? "00",
      icon: <CheckCircleRounded />,
      accent: accents.emerald
    },
    {
      label: "Canceladas no semestre",
      value: metricSummary?.semester_canceled ?? "00",
      icon: <CancelRounded />,
      accent: accents.rose
    }
  ];

  return (
    <Container>
      <MainContent>
        <Header>
          <HeaderText>
            <span className="eyebrow">Painel de controle</span>
            <h1>Bem-vindo de volta 👋</h1>
            <p>Acompanhe o desempenho das suas vendas e ordens em tempo real.</p>
          </HeaderText>
          <DatePill>
            <CalendarTodayRounded fontSize="small" />
            {today}
          </DatePill>
        </Header>

        {/* ===================== VENDAS ===================== */}
        <SectionLabel>
          <h3>
            <PointOfSaleRounded /> Vendas
          </h3>
        </SectionLabel>

        <StatGrid>
          {salesStats.map((s, i) => (
            <StatCard key={s.label} accent={s.accent} delay={i}>
              <StatIcon accent={s.accent}>{s.icon}</StatIcon>
              <StatBody>
                <label>{s.label}</label>
                <h2>{s.value}</h2>
                <small>{s.sub}</small>
              </StatBody>
            </StatCard>
          ))}
        </StatGrid>

        <SalesRow>
          <SalesByDayChart days={14} height={230} />
        </SalesRow>

        {/* ===================== FINANCEIRO ===================== */}
        {!isUser && (
          <>
            <SectionLabel>
              <h3>
                <AccountBalanceWalletRounded /> Resumo financeiro
              </h3>
              <Link
                to="/financial"
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: "var(--accent)",
                  textDecoration: "none",
                  whiteSpace: "nowrap"
                }}
              >
                Ver financeiro →
              </Link>
            </SectionLabel>

            <StatGrid>
              {financialStats.map((s, i) => (
                <StatCard key={s.label} accent={s.accent} delay={i}>
                  <StatIcon accent={s.accent}>{s.icon}</StatIcon>
                  <StatBody>
                    <label>{s.label}</label>
                    <h2>{s.value}</h2>
                  </StatBody>
                </StatCard>
              ))}
            </StatGrid>

            <SalesRow>
              <FinancialCashFlowChart data={financialCashFlow} height={230} />
            </SalesRow>
          </>
        )}

        {/* ===================== ORDENS ===================== */}
        <SectionLabel>
          <h3>
            <ReceiptLongRounded /> Ordens de Serviço
          </h3>
        </SectionLabel>

        <StatGrid>
          {orderStats.map((s, i) => (
            <StatCard key={s.label} accent={s.accent} delay={i}>
              <StatIcon accent={s.accent}>{s.icon}</StatIcon>
              <StatBody>
                <label>{s.label}</label>
                <h2>{s.value}</h2>
              </StatBody>
            </StatCard>
          ))}
        </StatGrid>

        <ChartsGrid>
          <div className="span-4">
            <OrdersSummaryDoughnut height={280} maxWidth="100%" />
          </div>
          <div className="span-8">
            <OrdersByMonthChart />
          </div>
          <div className="span-12">
            <NewVsOldClientsChart />
          </div>
        </ChartsGrid>
      </MainContent>
    </Container>
  );
};

export default Welcome;
